import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    LogOut,
    Crown,
    Inbox,
    Wallet,
    LayoutDashboard,
    CheckCircle2,
    ArrowRight,
    Lock,
    Newspaper,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
    getFirmDashboard,
    getFirmLeads,
    claimLead,
    getFirmTiers,
    createCheckout,
    getMeta,
    getFirmSession,
    setFirmSession,
    clearFirmSession,
    purchaseLead,
    getFirmBlogLeads,
    purchaseBlogLead,
    trackLeadView,
    getSpotlightSlots,
    purchaseSpotlight,
    subscribeFirm,
    getFirmSubscription,
    openBillingPortal,
    cancelFirmSubscription,
    resumeFirmSubscription,
} from "@/lib/api";

const TAB_LABEL = {
    dashboard: { label: "DASHBOARD", icon: LayoutDashboard },
    leads: { label: "CASE LEADS", icon: Inbox },
    blog_leads: { label: "BLOG LEADS", icon: Newspaper },
    billing: { label: "BILLING", icon: Wallet },
};

const TIER_BADGE = {
    starter: "border-white/40 text-white/70",
    professional: "border-white text-white",
    elite: "bg-[#FF3B30] border-[#FF3B30] text-white",
    enterprise: "bg-[#FFCC00] border-[#FFCC00] text-black",
};

export default function FirmPortal() {
    const nav = useNavigate();
    const [session, setSession] = useState(getFirmSession());
    const [tab, setTab] = useState("dashboard");
    const [meta, setMeta] = useState(null);
    const [dashboard, setDashboard] = useState(null);
    const [leads, setLeads] = useState([]);
    const [blogLeads, setBlogLeads] = useState([]);
    const [spotlightSlots, setSpotlightSlots] = useState([]);
    const [spotlightStateSlug, setSpotlightStateSlug] = useState("california");
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [upgrading, setUpgrading] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [portalLoading, setPortalLoading] = useState(false);
    const [billingInterval, setBillingInterval] = useState("month"); // "month" | "year"

    useEffect(() => {
        getMeta().then(setMeta).catch(() => {});
        getFirmTiers().then((d) => setTiers(d.tiers || [])).catch(() => {});
    }, []);

    const refresh = async () => {
        if (!session?.firm_id) return;
        setLoading(true);
        try {
            const [d, l, bl] = await Promise.all([
                getFirmDashboard(session.firm_id),
                getFirmLeads(session.firm_id, 25),
                getFirmBlogLeads(session.firm_id, 50).catch(() => ({ leads: [] })),
            ]);
            setDashboard(d);
            setLeads(l.leads || []);
            setBlogLeads(bl.leads || []);
            setFirmSession(d.firm);
            setSession(d.firm);
            // Pull live subscription status from Stripe (non-blocking)
            getFirmSubscription(session.firm_id)
                .then(setSubscription)
                .catch(() => setSubscription(null));
        } catch (e) {
            toast.error("Could not load firm portal");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.firm_id]);

    const claim = async (caseId) => {
        try {
            const r = await claimLead(session.firm_id, caseId);
            if (r.already_claimed) toast.info("Already claimed");
            else toast.success("Lead claimed");
            await refresh();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Claim failed";
            toast.error(msg);
        }
    };

    const buyLead = async (caseId) => {
        try {
            const r = await purchaseLead({
                firm_id: session.firm_id,
                case_id: caseId,
                origin_url: window.location.origin,
            });
            window.location.href = r.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Purchase failed");
        }
    };

    const buyBlogLead = async (leadId) => {
        try {
            // fire view-tracking (non-blocking) for scarcity counter
            trackLeadView(leadId, session.firm_id);
            const r = await purchaseBlogLead({
                firm_id: session.firm_id,
                lead_id: leadId,
                origin_url: window.location.origin,
            });
            if (r.trial_redeemed) {
                toast.success(
                    `Free trial credit used — lead claimed. ${r.trial_credits_remaining} free leads left.`,
                );
                await refresh();
                return;
            }
            window.location.href = r.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Purchase failed");
        }
    };

    useEffect(() => {
        getSpotlightSlots().then((d) => setSpotlightSlots(d.slots || [])).catch(() => {});
    }, []);

    const buySpotlight = async (slot_type) => {
        try {
            const body = {
                firm_id: session.firm_id,
                slot_type,
                origin_url: window.location.origin,
            };
            if (slot_type === "state") body.state_slug = spotlightStateSlug;
            const r = await purchaseSpotlight(body);
            window.location.href = r.url;
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg =
                typeof detail === "object"
                    ? `Slot taken until ${detail.ends_at?.slice(0, 10)} — try again then.`
                    : detail || "Purchase failed";
            toast.error(msg);
        }
    };

    const leadPriceFromScore = (score) => {
        const s = score || 0;
        if (s >= 90) return { tier: "S", price: 899 };
        if (s >= 75) return { tier: "A", price: 499 };
        if (s >= 60) return { tier: "B", price: 249 };
        return { tier: "C", price: 99 };
    };

    const upgrade = async (tier) => {
        if (!session?.firm_id) return;
        setUpgrading(true);
        try {
            // True recurring subscription (auto-renew, customer portal, proration)
            const r = await subscribeFirm({
                tier,
                firm_id: session.firm_id,
                origin_url: window.location.origin,
                interval: billingInterval,
            });
            window.location.href = r.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Checkout failed");
            setUpgrading(false);
        }
    };

    const manageBilling = async () => {
        if (!session?.firm_id) return;
        setPortalLoading(true);
        try {
            const r = await openBillingPortal(session.firm_id, window.location.origin);
            window.location.href = r.url;
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Could not open billing portal. Subscribe to a plan first.",
            );
            setPortalLoading(false);
        }
    };

    const cancelSub = async () => {
        if (!session?.firm_id) return;
        if (!window.confirm("Cancel auto-renewal at the end of the current billing period?")) return;
        try {
            const r = await cancelFirmSubscription(session.firm_id);
            toast.success("Subscription will end on " + (r.current_period_end || "").slice(0, 10));
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Cancel failed");
        }
    };

    const resumeSub = async () => {
        if (!session?.firm_id) return;
        try {
            await resumeFirmSubscription(session.firm_id);
            toast.success("Auto-renewal resumed");
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Resume failed");
        }
    };

    const logout = () => {
        clearFirmSession();
        nav("/hpm-law-firms");
    };

    if (!session?.firm_id) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-white">
                <Header />
                <div className="max-w-[800px] mx-auto px-6 py-32 text-center">
                    <Lock size={32} className="mx-auto text-[#FF3B30]" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mt-6 mb-2">
                        NOT SIGNED IN
                    </div>
                    <h1 className="font-anton text-4xl sm:text-6xl uppercase">
                        Sign in to your firm portal.
                    </h1>
                    <p className="font-sans text-white/60 mt-4 mb-8">
                        Register or log in from the law firm page.
                    </p>
                    <Link to="/hpm-law-firms" className="btn-brutal btn-brutal-red">
                        GO TO LAW FIRMS <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        );
    }

    const firm = dashboard?.firm || session;
    const stats = dashboard?.stats || {};
    const claims = dashboard?.claims || [];

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            <div className="max-w-[1500px] mx-auto px-6 sm:px-10 py-10">
                {/* Firm header */}
                <div
                    className="border-2 border-white/20 p-6 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6"
                    data-testid="firm-header"
                >
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                            FIRM PORTAL / {firm.access_code}
                        </div>
                        <h1 className="font-anton text-4xl sm:text-5xl uppercase leading-none">
                            {firm.firm_name}
                        </h1>
                        <div className="font-mono text-xs text-white/60 mt-2">
                            {firm.contact_email} · REFERRAL: {firm.referral_code}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span
                            className={`inline-block font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-2 border-2 ${TIER_BADGE[firm.tier] || ""}`}
                            data-testid="firm-tier-badge"
                        >
                            <Crown size={10} className="inline mr-1" /> {(firm.tier || "starter").toUpperCase()}
                        </span>
                        <button
                            onClick={logout}
                            className="btn-brutal text-xs"
                            data-testid="firm-logout"
                        >
                            <LogOut size={12} /> LOGOUT
                        </button>
                    </div>
                </div>

                {/* Trial banner */}
                {(firm.trial_credits || 0) - (firm.trial_credits_used || 0) > 0 && (
                    <div
                        className="border-2 border-[#FFCC00] bg-[#FFCC00]/5 p-4 mb-6 flex items-center justify-between gap-3 flex-wrap"
                        data-testid="firm-trial-banner"
                    >
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00]">
                                ✦ FREE TRIAL ACTIVE
                            </div>
                            <div className="font-anton text-2xl uppercase mt-1">
                                {(firm.trial_credits || 0) - (firm.trial_credits_used || 0)} free
                                blog leads on us
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mt-1">
                                NO CARD REQUIRED · CLAIM ANY OPEN LEAD WITH "USE FREE TRIAL"
                            </div>
                        </div>
                        <button
                            onClick={() => setTab("blog_leads")}
                            className="btn-brutal text-xs"
                            data-testid="firm-trial-cta"
                        >
                            BROWSE FREE LEADS →
                        </button>
                    </div>
                )}

                {/* Tabs */}
                <div className="grid grid-cols-2 md:grid-cols-4 border-2 border-white/20 mb-8" data-testid="firm-tabs">
                    {Object.entries(TAB_LABEL).map(([key, { label, icon: Icon }]) => {
                        const active = tab === key;
                        return (
                            <button
                                key={key}
                                onClick={() => setTab(key)}
                                data-testid={`firm-tab-${key}`}
                                className={`p-4 border-white/20 [&:not(:last-child)]:border-r-2 transition-colors ${active ? "bg-white text-black" : "hover:bg-white/5 text-white"}`}
                            >
                                <Icon size={16} className="inline mr-2" />
                                <span className="font-mono text-xs uppercase tracking-[0.25em]">
                                    {label}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* DASHBOARD */}
                {tab === "dashboard" && (
                    <div data-testid="tab-dashboard">
                        <div className="grid grid-cols-1 md:grid-cols-4 border-2 border-white/20 mb-8">
                            {[
                                {
                                    label: "LEADS / MO",
                                    value: `${stats.leads_claimed_this_month || 0} / ${stats.leads_limit_per_month || 0}`,
                                    accent: true,
                                },
                                {
                                    label: "TOTAL CASES",
                                    value: stats.total_cases_accessed || 0,
                                },
                                {
                                    label: "REFERRALS",
                                    value: stats.referral_count || 0,
                                },
                                {
                                    label: "STATUS",
                                    value: (firm.status || "trial").toUpperCase(),
                                },
                            ].map((s, i) => (
                                <div
                                    key={i}
                                    className="p-6 border-white/20 md:[&:not(:last-child)]:border-r-2 [&:not(:last-child)]:border-b-2 md:[&:not(:last-child)]:border-b-0"
                                >
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                                        {s.label}
                                    </div>
                                    <div
                                        className={`font-anton text-4xl uppercase leading-none ${s.accent ? "text-[#FF3B30]" : ""}`}
                                    >
                                        {s.value}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <h2 className="font-anton text-3xl uppercase mb-4">Recent claims</h2>
                        <div
                            className="border-2 border-white/20"
                            data-testid="recent-claims-list"
                        >
                            {claims.length === 0 && (
                                <div className="p-8 text-center font-mono text-xs uppercase tracking-wider text-white/40">
                                    NO LEADS CLAIMED YET
                                </div>
                            )}
                            {claims.map((c, i) => (
                                <div
                                    key={c.claim_id}
                                    className="p-5 border-white/20 [&:not(:last-child)]:border-b-2"
                                >
                                    <div className="flex justify-between items-baseline gap-4">
                                        <div>
                                            <div className="font-anton text-xl uppercase leading-none">
                                                {c.case_title}
                                            </div>
                                            <div className="font-mono text-[10px] uppercase tracking-wider text-white/50 mt-1">
                                                CASE #{(c.case_id || "").slice(0, 8).toUpperCase()} · CLAIMED{" "}
                                                {(c.claimed_at || "").slice(0, 10)}
                                            </div>
                                        </div>
                                        <Link
                                            to={`/hpm-analysis/${c.case_id}`}
                                            className="font-mono text-xs uppercase tracking-wider text-[#FF3B30] hover:text-white"
                                        >
                                            VIEW →
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* LEADS */}
                {tab === "leads" && (
                    <div data-testid="tab-leads">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                            QUALIFIED LEADS — MATCH YOUR PRACTICE
                        </div>
                        <h2 className="font-anton text-3xl uppercase mb-6">
                            {leads.length} available · {stats.leads_claimed_this_month || 0} /{" "}
                            {stats.leads_limit_per_month || 0} claimed this month
                        </h2>

                        {loading ? (
                            <div className="p-12 text-center">
                                <Loader2 size={24} className="animate-spin mx-auto text-[#FF3B30]" />
                            </div>
                        ) : leads.length === 0 ? (
                            <div className="border-2 border-dashed border-white/20 p-16 text-center">
                                <div className="font-anton text-2xl uppercase">
                                    No matching leads right now.
                                </div>
                                <p className="font-sans text-white/60 mt-2 text-sm">
                                    Adjust your firm's specialities or jurisdictions, or come back soon.
                                </p>
                            </div>
                        ) : (
                            <div
                                className="border-2 border-white/20"
                                data-testid="leads-list"
                            >
                                {leads.map((l, i) => (
                                    <div
                                        key={l.id}
                                        className="p-5 border-white/20 [&:not(:last-child)]:border-b-2 flex flex-col md:flex-row md:items-center justify-between gap-4"
                                        data-testid={`lead-${i}`}
                                    >
                                        <div className="flex-1">
                                            <div className="font-anton text-2xl uppercase leading-none">
                                                {l.case_title}
                                            </div>
                                            <div className="font-mono text-[10px] uppercase tracking-wider text-white/50 mt-2">
                                                {l.case_type?.replace(/_/g, " ")} · {l.jurisdiction} ·{" "}
                                                {l.language}
                                                {l.state ? ` · ${l.state}` : ""}
                                            </div>
                                            <div className="font-mono text-[10px] uppercase tracking-wider text-white/70 mt-2 flex gap-4">
                                                <span>
                                                    STRENGTH{" "}
                                                    <span className="text-[#FF3B30] font-bold">
                                                        {l.strength_score ?? "—"}
                                                    </span>
                                                </span>
                                                <span>VIOL. {l.violations_count}</span>
                                                <span>GAPS {l.evidence_gaps_count}</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Link
                                                to={`/hpm-analysis/${l.id}`}
                                                className="btn-brutal text-xs"
                                                data-testid={`lead-view-${i}`}
                                            >
                                                PREVIEW
                                            </Link>
                                            <button
                                                onClick={() => claim(l.id)}
                                                className="btn-brutal btn-brutal-red text-xs"
                                                data-testid={`lead-claim-${i}`}
                                            >
                                                CLAIM
                                            </button>
                                            {(() => {
                                                const lp = leadPriceFromScore(l.strength_score);
                                                return (
                                                    <button
                                                        onClick={() => buyLead(l.id)}
                                                        className="btn-brutal text-xs"
                                                        data-testid={`lead-buy-${i}`}
                                                        title={`Pay-per-lead — Tier ${lp.tier}`}
                                                    >
                                                        BUY ${lp.price}
                                                    </button>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* BLOG LEADS — readers captured via /hpm-blog funnel */}
                {tab === "blog_leads" && (
                    <div data-testid="tab-blog-leads">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-2">
                            BLOG LEADS · NON-EXCLUSIVE · UP TO 3 BUYERS PER LEAD
                        </div>
                        <h2 className="font-anton text-3xl uppercase mb-2">
                            {blogLeads.length} readers raised their hand
                        </h2>
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 mb-6">
                            Mid-intent leads from the legal-education funnel. Each lead is shared
                            with up to 3 firms in their state. Pricing: S=${"\u00a0"}699 · A=${"\u00a0"}349 · B=${"\u00a0"}149.
                        </p>

                        {loading ? (
                            <div className="p-12 text-center">
                                <Loader2 size={24} className="animate-spin mx-auto text-[#FF3B30]" />
                            </div>
                        ) : blogLeads.length === 0 ? (
                            <div className="border-2 border-dashed border-white/20 p-16 text-center">
                                <Newspaper size={32} className="text-white/30 mx-auto mb-4" />
                                <div className="font-anton text-2xl uppercase">
                                    No blog leads matching your jurisdiction yet.
                                </div>
                                <p className="font-sans text-white/60 mt-2 text-sm">
                                    Widen your states or specialities to see more, or keep checking —
                                    blog leads come in 24/7 from organic and social traffic.
                                </p>
                            </div>
                        ) : (
                            <div className="border-2 border-white/20" data-testid="blog-leads-list">
                                {blogLeads.map((l, i) => {
                                    const tierColor =
                                        l.tier === "S"
                                            ? "text-[#FF3B30] border-[#FF3B30]"
                                            : l.tier === "A"
                                            ? "text-[#FFCC00] border-[#FFCC00]"
                                            : "text-white border-white/50";
                                    const slotPct = Math.min(
                                        100,
                                        Math.round(((l.buyers_so_far || 0) / (l.max_buyers || 3)) * 100),
                                    );
                                    const hoursLeft = l.hours_left ?? 0;
                                    const expiringSoon = hoursLeft > 0 && hoursLeft < 12;
                                    return (
                                        <div
                                            key={l.lead_id}
                                            className="p-5 border-white/20 [&:not(:last-child)]:border-b-2 flex flex-col md:flex-row md:items-center justify-between gap-4"
                                            data-testid={`blog-lead-${i}`}
                                        >
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                    <span
                                                        className={`font-anton text-xl uppercase border-2 px-2 leading-tight ${tierColor}`}
                                                    >
                                                        TIER {l.tier}
                                                    </span>
                                                    {l.is_fresh && (
                                                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0a0a0a] bg-[#FFCC00] px-2 py-1 animate-pulse">
                                                            ✦ JUST IN
                                                        </span>
                                                    )}
                                                    {expiringSoon && (
                                                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF3B30] border-2 border-[#FF3B30] px-2 py-1">
                                                            ⚠ EXPIRES SOON
                                                        </span>
                                                    )}
                                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                                                        {l.case_type?.replace(/_/g, " ")} ·{" "}
                                                        {l.state || "—"} · ZIP {l.zip_code_prefix || "—"}
                                                    </span>
                                                </div>
                                                <div className="font-sans text-sm text-white/85 mb-2 italic">
                                                    "{l.situation_preview}"
                                                </div>
                                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 flex gap-3 flex-wrap mb-3">
                                                    <span className="text-[#FFCC00]">
                                                        👁 {l.viewers_so_far || 0} firms viewing
                                                    </span>
                                                    <span>
                                                        SOLD {l.buyers_so_far} / {l.max_buyers}
                                                    </span>
                                                    <span>
                                                        ⏱ {Math.floor(hoursLeft)}h left
                                                    </span>
                                                </div>
                                                {/* Scarcity bar */}
                                                <div className="h-1 bg-white/10 w-full max-w-xs">
                                                    <div
                                                        className="h-1 bg-[#FF3B30] transition-all"
                                                        style={{ width: `${slotPct}%` }}
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => buyBlogLead(l.lead_id)}
                                                className="btn-brutal btn-brutal-red text-xs whitespace-nowrap"
                                                data-testid={`blog-lead-buy-${i}`}
                                            >
                                                {(firm?.trial_credits || 0) - (firm?.trial_credits_used || 0) > 0
                                                    ? "USE FREE TRIAL"
                                                    : `BUY $${l.price?.toFixed(0)}`}{" "}
                                                <ArrowRight size={12} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* BILLING */}
                {tab === "billing" && (
                    <div data-testid="tab-billing">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                            BILLING / SUBSCRIPTION
                        </div>
                        <h2 className="font-anton text-3xl uppercase mb-6">
                            Current tier:{" "}
                            <span className="text-[#FF3B30]">{(firm.tier || "starter").toUpperCase()}</span>
                        </h2>

                        {/* Subscription state panel — only shown once subscribed */}
                        {subscription?.has_subscription && (
                            <div
                                className="border-2 border-white/20 p-6 mb-8 bg-white/5"
                                data-testid="subscription-status-panel"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">
                                            STRIPE SUBSCRIPTION STATUS
                                        </div>
                                        <div
                                            className={`font-anton text-2xl uppercase ${
                                                subscription.status === "active" || subscription.status === "trialing"
                                                    ? "text-[#00FF85]"
                                                    : subscription.status === "past_due"
                                                      ? "text-[#FFCC00]"
                                                      : "text-[#FF3B30]"
                                            }`}
                                            data-testid="subscription-status"
                                        >
                                            {subscription.status?.toUpperCase() || "UNKNOWN"}
                                            {subscription.cancel_at_period_end && " · ENDS SOON"}
                                        </div>
                                        {subscription.current_period_end && (
                                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mt-2">
                                                {subscription.cancel_at_period_end ? "ACCESS ENDS: " : "RENEWS: "}
                                                <span className="text-white">
                                                    {subscription.current_period_end.slice(0, 10)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-2 min-w-[200px]">
                                        <button
                                            onClick={manageBilling}
                                            disabled={portalLoading}
                                            className="btn-brutal btn-brutal-red w-full justify-center text-[10px]"
                                            data-testid="manage-billing-btn"
                                        >
                                            {portalLoading ? (
                                                <Loader2 size={12} className="animate-spin" />
                                            ) : (
                                                <>MANAGE BILLING →</>
                                            )}
                                        </button>
                                        {subscription.cancel_at_period_end ? (
                                            <button
                                                onClick={resumeSub}
                                                className="btn-brutal w-full justify-center text-[10px] border-2 border-white/40 text-white hover:bg-white hover:text-black transition-colors"
                                                data-testid="resume-subscription-btn"
                                            >
                                                RESUME AUTO-RENEW
                                            </button>
                                        ) : (
                                            <button
                                                onClick={cancelSub}
                                                className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-white/80 underline decoration-dotted underline-offset-4"
                                                data-testid="cancel-subscription-btn"
                                            >
                                                CANCEL AT PERIOD END
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-4 pt-4 border-t border-white/10">
                                    BILLED MONTHLY · AUTO-RENEW · MANAGE CARD / INVOICES / PLAN VIA STRIPE PORTAL
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-center mb-6" data-testid="billing-interval-toggle">
                            <div className="inline-flex border-2 border-white/20 p-1">
                                <button
                                    onClick={() => setBillingInterval("month")}
                                    className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                                        billingInterval === "month"
                                            ? "bg-[#FF3B30] text-white"
                                            : "text-white/60 hover:text-white"
                                    }`}
                                    data-testid="billing-interval-month"
                                >
                                    MONTHLY
                                </button>
                                <button
                                    onClick={() => setBillingInterval("year")}
                                    className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors flex items-center gap-2 ${
                                        billingInterval === "year"
                                            ? "bg-[#FF3B30] text-white"
                                            : "text-white/60 hover:text-white"
                                    }`}
                                    data-testid="billing-interval-year"
                                >
                                    ANNUAL
                                    <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 ${billingInterval === "year" ? "bg-white text-[#FF3B30]" : "bg-[#00FF85] text-black"}`}>
                                        SAVE 17%
                                    </span>
                                </button>
                            </div>
                        </div>

                        <div
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-2 border-white/20"
                            data-testid="billing-tiers"
                        >
                            {tiers.map((t, i) => {
                                const isCurrent = t.key === firm.tier;
                                const isAnnual = billingInterval === "year";
                                const displayPrice = isAnnual ? t.monthly_fee * 10 : t.monthly_fee;
                                const displayUnit = isAnnual ? "/ YR" : "/ MO";
                                return (
                                    <div
                                        key={t.key}
                                        className={`p-6 border-white/20 lg:[&:not(:nth-child(4n))]:border-r-2 [&:not(:last-child)]:border-b-2 lg:[&:nth-child(-n+4)]:border-b-0 ${isCurrent ? "bg-white/5" : ""}`}
                                        data-testid={`billing-tier-${t.key}`}
                                    >
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                                            TIER 0{i + 1}
                                        </div>
                                        <div className="font-anton text-2xl uppercase mt-1">
                                            {t.label}
                                        </div>
                                        <div className="font-anton text-4xl text-[#FF3B30] leading-none mt-2">
                                            ${displayPrice.toFixed(0)}
                                            <span className="text-white/40 text-sm ml-1">{displayUnit}</span>
                                        </div>
                                        {isAnnual && (
                                            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#00FF85] mt-1">
                                                SAVE ${(t.monthly_fee * 2).toFixed(0)} VS MONTHLY
                                            </div>
                                        )}
                                        <div className="font-mono text-[10px] uppercase tracking-wider text-white/50 mt-2 mb-4">
                                            {t.leads_limit_per_month >= 99999
                                                ? "UNLIMITED LEADS"
                                                : `${t.leads_limit_per_month} LEADS / MO`}
                                        </div>
                                        {isCurrent ? (
                                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white border-2 border-white px-3 py-2 text-center">
                                                <CheckCircle2 size={10} className="inline mr-1" /> CURRENT
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => upgrade(t.key)}
                                                disabled={upgrading}
                                                className="btn-brutal btn-brutal-red w-full justify-center text-[10px]"
                                                data-testid={`upgrade-${t.key}`}
                                            >
                                                {upgrading ? (
                                                    <Loader2 size={12} className="animate-spin" />
                                                ) : (
                                                    <>SUBSCRIBE</>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-4">
                            BILLED MONTHLY · AUTO-RENEW · CANCEL ANYTIME · POWERED BY STRIPE
                        </p>

                        {/* Spotlight sponsorships */}
                        {spotlightSlots.length > 0 && (
                            <div className="mt-12 border-t-2 border-white/20 pt-8" data-testid="spotlight-section">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-2">
                                    SPOTLIGHT SPONSORSHIPS · BE THE FIRM OF THE MONTH
                                </div>
                                <h2 className="font-anton text-3xl uppercase mb-2">
                                    Own the top of the page.
                                </h2>
                                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 mb-6">
                                    GET FEATURED ABOVE THE LEAD FUNNEL ON HPM'S HIGHEST-TRAFFIC PAGES.
                                    NON-EXCLUSIVE: 1 SPONSOR PER SLOT PER MONTH.
                                </p>
                                <div
                                    className="grid grid-cols-1 md:grid-cols-2 border-2 border-white/20"
                                    data-testid="spotlight-tiers"
                                >
                                    {spotlightSlots.map((s, i) => (
                                        <div
                                            key={s.slot_type}
                                            className={`p-6 border-white/20 ${i === 0 ? "md:border-r-2 border-b-2 md:border-b-0" : ""}`}
                                            data-testid={`spotlight-slot-${s.slot_type}`}
                                        >
                                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00]">
                                                {s.slot_type === "homepage" ? "NATIONAL" : "STATE-LEVEL"}
                                            </div>
                                            <div className="font-anton text-2xl uppercase mt-1">
                                                {s.label}
                                            </div>
                                            <div className="font-anton text-4xl text-[#FFCC00] leading-none mt-2">
                                                ${s.price.toFixed(0)}
                                                <span className="text-white/40 text-sm ml-1">
                                                    / {s.duration_days} DAYS
                                                </span>
                                            </div>
                                            <p className="font-sans text-sm text-white/70 mt-3 leading-relaxed">
                                                {s.description}
                                            </p>
                                            {s.slot_type === "state" && (
                                                <div className="mt-3">
                                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1 block">
                                                        STATE
                                                    </label>
                                                    <input
                                                        value={spotlightStateSlug}
                                                        onChange={(e) =>
                                                            setSpotlightStateSlug(
                                                                e.target.value.toLowerCase().replace(/\s+/g, "-"),
                                                            )
                                                        }
                                                        placeholder="california"
                                                        className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-xs text-white w-full focus:border-white outline-none"
                                                        data-testid="spotlight-state-input"
                                                    />
                                                </div>
                                            )}
                                            <button
                                                onClick={() => buySpotlight(s.slot_type)}
                                                className="btn-brutal btn-brutal-red w-full justify-center text-[10px] mt-4"
                                                data-testid={`spotlight-buy-${s.slot_type}`}
                                            >
                                                BECOME THE SPONSOR <ArrowRight size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <Footer disclaimer={meta?.disclaimer || ""} />
        </div>
    );
}
